import {
  WSActions,
  WSAlert,
  WSControl,
  WSControlGroup,
  WSElement,
  WSField,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSInfoBox,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSLabel,
  WSList,
  WSPanel,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RequirementExpirationStrategy,
  RequirementReviewStrategy
} from "@wingspanhq/payments/dist/interfaces/requirement";
import React, { useEffect, useMemo } from "react";
import * as Yup from "yup";
import { ObjectSchema } from "yup";
import { DataItem } from "../../../../../../shared/components/DataItem";
import { CreateRequirementDefinitionFormValues } from "../../../../../Requirements/types";
import { requirementDefinitionDefaultValidationSchema } from "../../../../components/CreateRequirementDefinitionForm/validationSchema";
import { RequirementDefinitionTemplate } from "../../types";
import {
  REVIEW_STRATEGY_ALWAYS_REQUIRED_COPY,
  REVIEW_STRATEGY_ON_FAIL_COPY
} from "./utils";
import {
  getExpirationStrategyDefaultValues,
  getReviewStrategyDefaultValues
} from "../../../../components/CreateRequirementDefinitionForm/utils";
import { useFormContext } from "react-hook-form";

type CreateRequirementDefinitionFormProps = {
  formData?: CreateRequirementDefinitionFormValues;
  template: RequirementDefinitionTemplate;
  onBack: () => void;
  onSuccess: (formData: CreateRequirementDefinitionFormValues) => void;
};

const getDefaultValues = (
  template: RequirementDefinitionTemplate,
  formData?: CreateRequirementDefinitionFormValues
): Partial<CreateRequirementDefinitionFormValues> =>
  formData
    ? formData
    : {
        name: template.isCustomName ? "" : template.name,
        description: template.modal.description || "",
        type: template.requirementType,
        contextType: Array.isArray(template.contextType)
          ? template.contextType[0]
          : template.contextType,
        ...getReviewStrategyDefaultValues(
          template.isRequirementWithErrorStates
        ),
        ...getExpirationStrategyDefaultValues(!template.noExpirationForm),
        ...template.form?.getDefaultValues(template)
      };

const HiddenFields: React.FC<{ template: RequirementDefinitionTemplate }> = ({
  template
}) => {
  const { register } = useFormContext();

  useEffect(() => {
    const defaultFields = template.form?.getDefaultValues(template);

    if (!template.form?.partialForm && defaultFields) {
      Object.keys(defaultFields).forEach(key => {
        register(key);
      });
    }
  }, [template, register]);

  return null;
};

export const CreateRequirementDefinitionByTemplateForm: React.FC<
  CreateRequirementDefinitionFormProps
> = ({ onBack, onSuccess, template, formData }) => {
  const defaultValues = useMemo(
    () => getDefaultValues(template, formData),
    [template, formData]
  );

  const partialForm: React.ReactNode = useMemo(() => {
    return template.form?.partialForm || null;
  }, [template.form]);

  const validationSchema = useMemo(() => {
    let validations = template.form?.validationSchema
      ? template.form?.validationSchema
      : Yup.object().shape({});

    return requirementDefinitionDefaultValidationSchema(template).concat(
      validations as ObjectSchema<any>
    );
  }, [template.form]);

  return (
    <WSForm<CreateRequirementDefinitionFormValues>
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={async formValues => {
        onSuccess(formValues);
      }}
    >
      <HiddenFields template={template} />
      <WSList gap="XL">
        <WSPanel>
          <WSList gap="XL">
            <WSText.Paragraph weight="medium" mb="S">
              Requirement information
            </WSText.Paragraph>

            <WSGrid gutter={{ m: "2XL" }}>
              <WSGrid.Item span={{ m: "6" }}>
                {template.isCustomName ? (
                  <WSForm.Field
                    name="name"
                    label="Name"
                    placeholder="Requirement name"
                    component={WSInputText}
                    componentProps={{
                      required: true
                    }}
                  />
                ) : (
                  <>
                    <DataItem value={template.name} label="Name" />
                    <WSForm.Field hidden name="name" component={WSInputText} />
                  </>
                )}
              </WSGrid.Item>

              <WSGrid.Item span={{ m: "6" }}>
                <DataItem
                  icon={template.icon}
                  value={template.name}
                  secondaryText={template.helperText}
                  label="Type"
                />
                <WSForm.Field hidden name="type" component={WSInputText} />
              </WSGrid.Item>
            </WSGrid>

            {template.isBillable && template.costPerSubmission ? (
              <WSAlert
                theme="info"
                title="About billable requirements"
                icon="info-circle"
              >
                Contractor(s) assigned with this requirement will be asked to
                complete the {template.name} requirement.
                <br />
                <br />
                {template.name} is a billable requirement and will be billed to
                you, the client, at ${template.costPerSubmission} for every
                successful verification.
              </WSAlert>
            ) : null}

            {template.payerInfo ? (
              <WSInfoBox theme="info">
                <WSList.Horizontal gap="S">
                  <WSText.ParagraphSm>Description</WSText.ParagraphSm>
                  <WSPill text="Private" icon="lock" theme="neutral" />
                </WSList.Horizontal>
                <WSFlexBox direction="column" gap="L">
                  <WSElement>
                    <WSLabel text="Description" />
                    <WSText.ParagraphSm weight="book" color="gray600">
                      {template.payerInfo.description}
                    </WSText.ParagraphSm>
                  </WSElement>

                  {template.payerInfo.exampleUseCases && (
                    <WSElement>
                      <WSLabel text="Example use cases" />
                      <WSText.ParagraphSm weight="book" color="gray600">
                        {template.payerInfo.exampleUseCases}
                      </WSText.ParagraphSm>
                    </WSElement>
                  )}
                  {template.payerInfo.disclaimer && (
                    <WSElement>
                      <WSLabel text="Disclaimer" />
                      <WSText.ParagraphSm weight="book" color="gray600">
                        {template.payerInfo.disclaimer}
                      </WSText.ParagraphSm>
                    </WSElement>
                  )}
                  <WSElement>
                    <WSLabel text="Cost" />
                    <WSText.ParagraphSm weight="book" color="gray600">
                      {template.payerInfo.cost}
                    </WSText.ParagraphSm>
                  </WSElement>
                </WSFlexBox>
                <WSForm.Field
                  hidden
                  name="description"
                  component={WSInputText}
                />
              </WSInfoBox>
            ) : (
              <WSForm.Field
                name="description"
                label="Description"
                hidden={template.payerInfo !== null}
                placeholder="Requirement description"
                componentProps={{
                  helperText:
                    "The description is visible internally to you and your team only"
                }}
                component={WSInputTextarea}
              />
            )}
          </WSList>
        </WSPanel>

        {partialForm}

        <WSForm.Field hidden name="contextType" component={WSInputText} />

        <WSPanel>
          <WSList gap="XL">
            <WSList gap="M">
              <WSText.ParagraphSm color="gray700">
                Expiration date
              </WSText.ParagraphSm>

              <WSInfoBox theme="info" hidden={!template.noExpirationForm}>
                This requirement type will automatically extract the expiration
                date from the submitted document or file used to fulfill the
                requirement.
              </WSInfoBox>
              <WSForm.Field
                name="expirationStrategy"
                hidden={template.noExpirationForm}
                componentProps={{
                  type: "radio",
                  size: "S",
                  options: [
                    {
                      value: "None",
                      helperText:
                        "The requirement will have no expiration date and will never expire.",
                      label: "Never expires"
                    },
                    {
                      value: RequirementExpirationStrategy.DaysAfterYearStart,
                      helperText:
                        "The requirement will expire after a fixed period after the date that the year starts.",
                      label: "Fixed period from year start"
                    },
                    {
                      value: RequirementExpirationStrategy.DaysAfterComplete,
                      helperText:
                        "The requirement will expire after a fixed period after the date that the requirement is completed.",
                      label: "Fixed period from completion"
                    },
                    {
                      value: RequirementExpirationStrategy.DaysBeforeYearEnd,
                      helperText:
                        "The requirement will expire after a fixed period before the date that the year ends.",
                      label: "Fixed period before year end"
                    }
                  ]
                }}
                component={WSControlGroup}
              />
              <WSForm.Value name="expirationStrategy">
                {(expirationStrategy: RequirementExpirationStrategy) => (
                  <WSInfoBox
                    theme="info"
                    hidden={
                      ![
                        RequirementExpirationStrategy.DaysAfterYearStart,
                        RequirementExpirationStrategy.DaysBeforeYearEnd,
                        RequirementExpirationStrategy.DaysAfterComplete
                      ].includes(expirationStrategy) ||
                      template.noExpirationForm
                    }
                  >
                    <WSElement>
                      <WSForm.Field
                        name="expirationDays"
                        label="Number of days"
                        componentProps={{
                          required: true,
                          min: 1
                        }}
                        component={WSInputNumber}
                      />
                    </WSElement>
                  </WSInfoBox>
                )}
              </WSForm.Value>
            </WSList>
            <WSList gap="M">
              <WSText.ParagraphSm color="gray700">
                Early renewals (optional)
              </WSText.ParagraphSm>
              <WSForm.Field
                name="manualRenewalAllowed"
                componentProps={{
                  type: "checkbox",
                  size: "S",
                  label:
                    "Allow contractor to renew their documents for this requirement at any time",
                  helperText:
                    "We'll automatically remind contractors 30 days before an expiration date. \nCheck this box if you want to make renewals available anytime. \nAs the payer, you can also request renewals manually from the contractor’s requirement detail page."
                }}
                component={WSControl}
              />
            </WSList>
          </WSList>
        </WSPanel>

        <WSPanel>
          <WSField
            label="Approvals"
            helperText="Set whether your approval is required when contractor submits a requirement"
            mb="M"
          />
          {template.isRequirementWithErrorStates ? (
            <WSForm.Field
              name="reviewStrategy"
              componentProps={{
                type: "radio",
                size: "S",
                options: [
                  {
                    value: RequirementReviewStrategy.OnFail,
                    label: REVIEW_STRATEGY_ON_FAIL_COPY
                  },
                  {
                    value: RequirementReviewStrategy.AlwaysRequired,
                    label: REVIEW_STRATEGY_ALWAYS_REQUIRED_COPY
                  }
                ]
              }}
              component={WSControlGroup}
            />
          ) : (
            <WSForm.Value name="isApprovalRequired">
              {(isApprovalRequired: boolean) => (
                <WSForm.Field
                  name="isApprovalRequired"
                  label={`Approvals ${isApprovalRequired ? "on" : "off"}`}
                  componentProps={{
                    type: "switch",
                    size: "S",
                    defaultValue: template.isRequirementWithErrorStates
                      ? "true"
                      : "false"
                  }}
                  component={WSControl}
                />
              )}
            </WSForm.Value>
          )}
        </WSPanel>

        {/*<WSForm.Debug />*/}

        <WSForm.Data<CreateRequirementDefinitionFormValues>>
          {formData => (
            <WSActions
              alignment="fill"
              orientation="horizontal"
              buttons={[
                {
                  kind: "Tertiary",
                  label: "Cancel",
                  onClick: onBack
                },
                {
                  kind: "Primary",
                  label: "Continue",
                  type: "submit",
                  disabled: !validationSchema.isValidSync(formData)
                }
              ]}
            />
          )}
        </WSForm.Data>
      </WSList>
    </WSForm>
  );
};
